import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../components/common/Sidebar";
import { getMediaUrl, getNewCreations } from "../services/catalogueService";
import "./NewCreation.css";

const isActiveNewCreation = (item) => {
  const activeValue = item?.isActive ?? item?.IsActive ?? true;
  return activeValue === true || activeValue === 1 || activeValue === "true";
};

const getNewCreationTitle = (item) => item?.title || item?.Name || "New Creation";

const getNewCreationImage = (item) =>
  item?.imageUrl || item?.OriginalPhoto || item?.CanvasPhoto || item?.ThumbPhoto || "";

const getNewCreationThumbnail = (item) =>
  item?.thumbnailUrl ||
  item?.ThumbPhoto ||
  item?.imageUrl ||
  item?.CanvasPhoto ||
  item?.OriginalPhoto ||
  "";

export default function NewCreation() {
  const { page } = useParams();
  const [newCreations, setNewCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgDimensions, setImgDimensions] = useState({ width: 250, height: 250 });
  const [containerReady, setContainerReady] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const resizeTimerRef = useRef(null);
  const slideshowTimerRef = useRef(null);
 
  // Pagination logic (Moved up to prevent ReferenceError in hooks)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = newCreations.slice(indexOfFirstItem, indexOfLastItem);
 
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setImgLoaded(false);
    setContainerReady(false);
    setIsClosing(false);
    setIsPlaying(true);
    setImgDimensions({ width: 250, height: 250 });
    setLightboxOpen(true);
    requestAnimationFrame(() => setOverlayVisible(true));
  };

  const closeLightbox = () => {
    setIsClosing(true);
    setOverlayVisible(false);
    setIsPlaying(false);
    setTimeout(() => {
      setLightboxOpen(false);
      setIsClosing(false);
      setImgLoaded(false);
      setContainerReady(false);
    }, 200);
  };

  const navigateLightbox = useCallback((newIndex) => {
    setImgLoaded(false);
    setContainerReady(false);
    setLightboxIndex(newIndex);
  }, []);

  const nextLightboxImage = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!newCreations.length) return;
    setImgLoaded(false);
    setContainerReady(false);
    setLightboxIndex((prev) => (prev + 1) % newCreations.length);
  }, [newCreations.length]);

  const prevLightboxImage = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!newCreations.length) return;
    setIsPlaying(false);
    setImgLoaded(false);
    setContainerReady(false);
    setLightboxIndex((prev) => (prev === 0 ? newCreations.length - 1 : prev - 1));
  };

  const togglePlay = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsPlaying((prev) => !prev);
  };

  const handleLightboxImgLoad = useCallback((e) => {
    const img = e.target;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    const maxW = Math.min(window.innerWidth * 0.72, 680);
    const maxH = window.innerHeight - 170;
    let w = natW;
    let h = natH;
    if (w > maxW) { h = h * (maxW / w); w = maxW; }
    if (h > maxH) { w = w * (maxH / h); h = maxH; }
    setImgDimensions({ width: Math.round(w), height: Math.round(h) });
    clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      setContainerReady(true);
      setTimeout(() => setImgLoaded(true), 1000);
    }, 100);
  }, []);
 
  useEffect(() => {
    if (lightboxOpen && isPlaying && imgLoaded) {
      clearTimeout(slideshowTimerRef.current);
      slideshowTimerRef.current = setTimeout(() => {
        nextLightboxImage();
      }, 6000);
    }
    return () => clearTimeout(slideshowTimerRef.current);
  }, [lightboxOpen, isPlaying, imgLoaded, lightboxIndex, nextLightboxImage]);

  useEffect(() => {
    if (page) {
      setCurrentPage(Math.max(Number(page) || 1, 1));
    }
  }, [page]);

  useEffect(() => {
    const fetchNewCreations = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getNewCreations();
        const activeNewCreations = data.filter(isActiveNewCreation).sort(
          (a, b) =>
            new Date(b.createdAt || 0) - new Date(a.createdAt || 0) ||
            Number(b.sequence || b.Id || 0) - Number(a.sequence || a.Id || 0)
        );

        setNewCreations(activeNewCreations);
      } catch (err) {
        console.error("Failed to load new creations:", err);
        setError("Unable to load new creation items right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchNewCreations();
  }, []);

  const totalPages = Math.ceil(newCreations.length / itemsPerPage);

  const paginate = (pageNumber, event) => {
    event?.preventDefault();
    setCurrentPage(pageNumber);
    window.requestAnimationFrame(() => {
      document.querySelector(".rawcast-page .catalogue-header")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const getPageNumbers = () =>
    Array.from({ length: totalPages }, (_, index) => index + 1);

  return (
    <>
      <Sidebar showNewCreation={true} />
      <main className="content-area rawcast-page">
        <div className="catalogue-header">
          <h2>New Creation</h2>
        </div>

        {loading ? <p className="catalogue-status">Loading catalogue...</p> : null}
        {error ? <p className="catalogue-status error">{error}</p> : null}

        {!loading && !error ? (
          newCreations.length > 0 ? (
            <>
              <div className="product-grid">
                {currentItems.map((item, index) => {
                  const imageSrc = getMediaUrl(getNewCreationThumbnail(item));
                  const title = getNewCreationTitle(item);

                  return (
                    <button
                      type="button"
                      className="product-card rawcast-card"
                      key={item._id || title}
                      onClick={() => openLightbox(index)}
                      aria-label={`Open ${title}`}
                    >
                      {imageSrc ? (
                        <span className="rawcast-thumb-frame">
                          <img
                            className="product-image rawcast-thumb"
                            src={imageSrc}
                            alt={title}
                          />
                        </span>
                      ) : (
                        <div className="product-image-placeholder rawcast-thumb-frame">
                          <span>No Image</span>
                        </div>
                      )}

                      <div className="product-info">
                        <h4 className="product-name">{title}</h4>
                      </div>
                    </button>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    type="button"
                    onClick={(event) => paginate(1, event)}
                    disabled={currentPage === 1}
                    className="page-btn"
                  >
                    &laquo;
                  </button>
                  <button
                    type="button"
                    onClick={(event) => paginate(currentPage - 1, event)}
                    disabled={currentPage === 1}
                    className="page-btn"
                  >
                    &lsaquo;
                  </button>

                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      type="button"
                      onClick={(event) => paginate(number, event)}
                      className={`page-btn ${currentPage === number ? "active" : ""}`}
                    >
                      {number}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={(event) => paginate(currentPage + 1, event)}
                    disabled={currentPage === totalPages}
                    className="page-btn"
                  >
                    &rsaquo;
                  </button>
                  <button
                    type="button"
                    onClick={(event) => paginate(totalPages, event)}
                    disabled={currentPage === totalPages}
                    className="page-btn"
                  >
                    &raquo;
                  </button>
                  <span className="pagination-count">
                    {currentPage} of {totalPages}
                  </span>
                </div>
              )}
            </>
          ) : (
            <p className="catalogue-status">No catalogue items available yet.</p>
          )
        ) : null}

        {lightboxOpen && currentItems.length > 0 && (
          <>
            <div
              className={`vlb-overlay ${overlayVisible && !isClosing ? 'vlb-overlay--visible' : ''}`}
              onClick={closeLightbox}
              aria-hidden="true"
            />
            <div
              className={`vlb-lightbox ${overlayVisible && !isClosing ? 'vlb-lightbox--visible' : ''}`}
              onClick={closeLightbox}
            >
              <div className="vlb-stage" onClick={(e) => e.stopPropagation()}>
                <div
                  className="vlb-outer-container"
                  style={{
                    width: imgDimensions.width + 4,
                    height: imgDimensions.height + 4,
                  }}
                >
                  <div className="vlb-image-container">
                    {!imgLoaded && (
                      <div className="vlb-loading">
                        <div className="vlb-spinner" />
                      </div>
                    )}
                    <button className="vlb-hover-prev" onClick={prevLightboxImage} aria-label="Previous">prev</button>
                    <button className="vlb-hover-next" onClick={nextLightboxImage} aria-label="Next">next</button>
                    <img
                      className={`vlb-img ${imgLoaded ? 'vlb-img--visible' : ''}`}
                      src={getMediaUrl(getNewCreationImage(currentItems[lightboxIndex]))}
                      alt={getNewCreationTitle(currentItems[lightboxIndex])}
                      onLoad={handleLightboxImgLoad}
                      key={`lb-${lightboxIndex}`}
                    />
                  </div>
                </div>
                <div className={`vlb-data ${imgLoaded ? 'vlb-data--visible' : ''}`}>
                  <div className="vlb-data-inner">
                    <div className="vlb-info">
                      <div className="vlb-caption">{getNewCreationTitle(currentItems[lightboxIndex])}</div>
                      <div className="vlb-meta">
                        <div className="vlb-counter">{lightboxIndex + 1} / {currentItems.length}</div>
                        <div className="vlb-controls">
                          <button className="vlb-ctrl-btn vlb-ctrl-prev" onClick={prevLightboxImage} aria-label="Previous">prev</button>
                          <button 
                            className={`vlb-ctrl-btn vlb-ctrl-play ${!isPlaying ? 'vlb-ctrl-paused' : ''}`} 
                            onClick={togglePlay} 
                            style={{ backgroundPosition: isPlaying ? '-48px bottom' : '-24px bottom' }}
                            aria-label={isPlaying ? "Pause" : "Play"}
                          >
                            {isPlaying ? "pause" : "play"}
                          </button>
                          <button className="vlb-ctrl-btn vlb-ctrl-next" onClick={nextLightboxImage} aria-label="Next">next</button>
                        </div>
                      </div>
                    </div>
                    <button className="vlb-close" onClick={closeLightbox} aria-label="Close">✕</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </>
  );
}
